import { useState, useEffect } from 'react';
import {
  Shield,
  Check,
  X,
  Loader2,
  ChevronDown,
  ChevronRight,
  Plus,
  Edit2,
  Trash2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/hooks/usePermissions';
import { useCustomRoles, type CustomRole } from '@/hooks/useCustomRoles';
import { useAuth } from '@/hooks/useAuth';

const ROLE_DESCRIPTIONS: Record<string, string> = {
  superadmin: 'Full system access across all organizations',
  owner: 'Full access to organization settings and all features',
  admin: 'Manage team, menu, and most business operations',
  inventory_manager: 'Full control over inventory, purchase orders, and transfers',
  manager: 'Day-to-day operations, limited admin access',
  inventory: 'Basic inventory access and stock counting',
  cashier: 'Basic POS and cash handling',
  employee: 'Basic POS and order access',
};

const SYSTEM_ROLES = ['superadmin', 'owner', 'admin', 'inventory_manager', 'manager', 'inventory', 'cashier', 'employee'];

const ROLE_COLORS: Record<string, string> = {
  superadmin: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
  owner: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  admin: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  inventory_manager: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
  manager: 'bg-green-500/10 text-green-600 border-green-500/20',
  inventory: 'bg-teal-500/10 text-teal-600 border-teal-500/20',
  cashier: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
  employee: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
};

const COLOR_OPTIONS = [
  { value: '#6366f1', label: 'Indigo' },
  { value: '#8b5cf6', label: 'Violet' },
  { value: '#ec4899', label: 'Pink' },
  { value: '#ef4444', label: 'Red' },
  { value: '#f97316', label: 'Orange' },
  { value: '#eab308', label: 'Yellow' },
  { value: '#22c55e', label: 'Green' },
  { value: '#14b8a6', label: 'Teal' },
  { value: '#06b6d4', label: 'Cyan' },
  { value: '#3b82f6', label: 'Blue' },
];

export default function Roles() {
  const { permissionsByCategory, rolePermissions, isLoading, permissions } = usePermissions();
  const { customRoles, createCustomRole, updateCustomRole, deleteCustomRole, updateRolePermissions, fetchRolePermissions } = useCustomRoles();
  const { isSuperadmin, profile } = useAuth();
  const { hasPermission } = usePermissions();
  
  const canManageRoles = hasPermission('roles.create') || profile?.role === 'owner' || profile?.role === 'admin' || isSuperadmin;
  
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  
  // Custom role dialog state
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<CustomRole | null>(null);
  const [roleForm, setRoleForm] = useState({
    name: '',
    description: '',
    base_role: '',
    color: '#6366f1',
  });
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [roleSaving, setRoleSaving] = useState(false);
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<CustomRole | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Filter out superadmin role for non-superadmins
  const visibleSystemRoles = isSuperadmin
    ? SYSTEM_ROLES
    : SYSTEM_ROLES.filter(role => role !== 'superadmin');

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const hasRolePermission = (role: string, permissionCode: string): boolean => {
    const rolePerms = rolePermissions[role] || [];
    return rolePerms.includes('*') || rolePerms.includes(permissionCode);
  };

  // Filter out superadmin-only permissions for non-superadmins
  const SUPERADMIN_ONLY_PERMISSIONS = ['audit.view_superadmin', 'audit.purge'];

  const filteredPermissionsByCategory = Object.entries(permissionsByCategory).reduce(
    (acc, [category, perms]) => {
      const filteredPerms = isSuperadmin
        ? perms
        : perms.filter(p => !SUPERADMIN_ONLY_PERMISSIONS.includes(p.code));

      if (filteredPerms.length > 0) {
        acc[category] = filteredPerms;
      }
      return acc;
    },
    {} as typeof permissionsByCategory
  );

  const categories = Object.keys(filteredPermissionsByCategory);

  // Reset form
  const resetForm = () => {
    setRoleForm({ name: '', description: '', base_role: '', color: '#6366f1' });
    setSelectedPermissions([]);
    setEditingRole(null);
  };

  // Open create dialog
  const openCreateDialog = () => {
    resetForm();
    setRoleDialogOpen(true);
  };

  // Open edit dialog
  const openEditDialog = async (role: CustomRole) => {
    setEditingRole(role);
    setRoleForm({
      name: role.name,
      description: role.description || '',
      base_role: role.base_role || '',
      color: role.color || '#6366f1',
    });
    
    // Fetch role's permissions
    try {
      const permIds = await fetchRolePermissions(role.id);
      setSelectedPermissions(permIds);
    } catch {
      setSelectedPermissions([]);
    }
    
    setRoleDialogOpen(true);
  };

  // Handle save
  const handleSaveRole = async () => {
    if (!roleForm.name.trim()) return;
    
    setRoleSaving(true);
    try {
      if (editingRole) {
        await updateCustomRole.mutateAsync({
          id: editingRole.id,
          updates: {
            name: roleForm.name.trim(),
            description: roleForm.description.trim() || undefined,
            base_role: roleForm.base_role || null,
            color: roleForm.color,
          },
        });
        await updateRolePermissions.mutateAsync({
          roleId: editingRole.id,
          permissionIds: selectedPermissions,
        });
      } else {
        await createCustomRole.mutateAsync({
          name: roleForm.name.trim(),
          description: roleForm.description.trim() || undefined,
          base_role: roleForm.base_role || undefined,
          color: roleForm.color,
          permissions: selectedPermissions,
        });
      }
      setRoleDialogOpen(false);
      resetForm();
    } finally {
      setRoleSaving(false);
    }
  };

  // Handle delete
  const handleDeleteRole = async () => {
    if (!roleToDelete) return;
    
    setDeleting(true);
    try {
      await deleteCustomRole.mutateAsync(roleToDelete.id);
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  // Toggle permission selection
  const togglePermission = (permissionId: string) => {
    setSelectedPermissions(prev =>
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  // Get base role permissions as pre-selected
  useEffect(() => {
    if (roleForm.base_role && !editingRole) {
      const basePerms = rolePermissions[roleForm.base_role] || [];
      if (basePerms.includes('*')) {
        setSelectedPermissions(permissions?.map(p => p.id) || []);
      } else {
        const permIds = permissions?.filter(p => basePerms.includes(p.code)).map(p => p.id) || [];
        setSelectedPermissions(permIds);
      }
    }
  }, [roleForm.base_role, permissions, rolePermissions, editingRole]);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold">Role & Permission Management</h2>
          <p className="text-sm text-muted-foreground">
            View system roles and create custom roles for your organization
          </p>
        </div>
        {canManageRoles && (
          <Button onClick={openCreateDialog} className="gap-2">
            <Plus className="h-4 w-4" />
            Create Custom Role
          </Button>
        )}
      </div>

      {/* Custom Roles Section */}
      {customRoles.length > 0 && (
        <div className="mb-8">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">
            Custom Roles
          </h3>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {customRoles.map(role => (
              <Card key={role.id} className="overflow-hidden">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className="h-5 w-5 rounded-full"
                        style={{ backgroundColor: role.color }}
                      />
                      <CardTitle className="text-base">{role.name}</CardTitle>
                    </div>
                    {canManageRoles && (
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openEditDialog(role)}
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => {
                            setRoleToDelete(role);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                    {role.description || 'Custom organization role'}
                  </p>
                  <div className="flex items-center gap-2">
                    {role.base_role && (
                      <Badge variant="outline" className="text-xs">
                        Based on: {role.base_role}
                      </Badge>
                    )}
                    <Badge variant="secondary" className="text-xs">
                      Custom
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* System Roles Section */}
      <div className="mb-8">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">
          System Roles
        </h3>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {visibleSystemRoles.map(role => (
            <Card key={role} className="overflow-hidden">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <Shield className={cn("h-5 w-5", ROLE_COLORS[role]?.split(' ')[1])} />
                  <CardTitle className="text-base capitalize">{role.replace('_', ' ')}</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">
                  {ROLE_DESCRIPTIONS[role]}
                </p>
                <Badge variant="outline" className={cn("text-xs", ROLE_COLORS[role])}>
                  {rolePermissions[role]?.includes('*')
                    ? 'All permissions'
                    : `${rolePermissions[role]?.length || 0} permissions`}
                </Badge>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Permission Matrix */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Permission Matrix</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3 sticky left-0 bg-muted/50 min-w-[200px]">
                      Permission
                    </th>
                    {visibleSystemRoles.map(role => (
                      <th
                        key={role}
                        className="text-center text-sm font-medium text-muted-foreground px-3 py-3 capitalize"
                      >
                        {role === 'inventory_manager' ? 'Inv. Mgr' : role}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {categories.map(category => (
                    <>
                      <tr
                        key={category}
                        className="bg-muted/30 cursor-pointer hover:bg-muted/50"
                        onClick={() => toggleCategory(category)}
                      >
                        <td
                          colSpan={visibleSystemRoles.length + 1}
                          className="px-4 py-2 font-medium text-sm capitalize"
                        >
                          <div className="flex items-center gap-2">
                            {expandedCategories.includes(category) ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                            {category}
                            <Badge variant="secondary" className="ml-2">
                              {filteredPermissionsByCategory[category]?.length || 0}
                            </Badge>
                          </div>
                        </td>
                      </tr>
                      {expandedCategories.includes(category) &&
                        filteredPermissionsByCategory[category]?.map(permission => (
                          <tr key={permission.id} className="border-b hover:bg-muted/20">
                            <td className="px-4 py-2 sticky left-0 bg-background">
                              <div className="pl-6">
                                <p className="text-sm font-medium">{permission.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {permission.code}
                                </p>
                              </div>
                            </td>
                            {visibleSystemRoles.map(role => (
                              <td key={role} className="px-3 py-2 text-center">
                                {hasRolePermission(role, permission.code) ? (
                                  <Check className="h-4 w-4 text-green-600 mx-auto" />
                                ) : (
                                  <X className="h-4 w-4 text-muted-foreground/30 mx-auto" />
                                )}
                              </td>
                            ))}
                          </tr>
                        ))}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Note */}
      <p className="text-sm text-muted-foreground mt-4">
        Note: Individual user permissions can be overridden from the Team page.
        Create custom roles above for organization-specific access control.
      </p>

      {/* Create/Edit Role Dialog */}
      <ResponsiveModal open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <ResponsiveModalContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>
              {editingRole ? 'Edit Custom Role' : 'Create Custom Role'}
            </ResponsiveModalTitle>
            <ResponsiveModalDescription>
              {editingRole
                ? 'Modify the role settings and permissions'
                : 'Create a new custom role with specific permissions'}
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>
          
          <ResponsiveModalBody className="flex-1 overflow-y-auto">
            <div className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="roleName">Role Name *</Label>
                  <Input
                    id="roleName"
                    value={roleForm.name}
                    onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                    placeholder="e.g., Shift Lead"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="baseRole">Base Role (Optional)</Label>
                  <Select
                    value={roleForm.base_role || 'none'}
                    onValueChange={(value) => setRoleForm({ ...roleForm, base_role: value === 'none' ? '' : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Start from scratch" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Start from scratch</SelectItem>
                      {SYSTEM_ROLES.filter(r => r !== 'superadmin').map(role => (
                        <SelectItem key={role} value={role} className="capitalize">
                          {role.replace('_', ' ')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Inherit permissions from an existing role
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="roleDescription">Description</Label>
                <Textarea
                  id="roleDescription"
                  value={roleForm.description}
                  onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                  placeholder="Describe what this role is for..."
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Color</Label>
                <div className="flex flex-wrap gap-2">
                  {COLOR_OPTIONS.map(color => (
                    <button
                      key={color.value}
                      type="button"
                      className={cn(
                        'w-8 h-8 rounded-full border-2 transition-all',
                        roleForm.color === color.value
                          ? 'border-foreground scale-110'
                          : 'border-transparent hover:scale-105'
                      )}
                      style={{ backgroundColor: color.value }}
                      onClick={() => setRoleForm({ ...roleForm, color: color.value })}
                      title={color.label}
                    />
                  ))}
                </div>
              </div>

              {/* Permissions */}
              <div className="space-y-2">
                <Label>Permissions</Label>
                <div className="border rounded-lg max-h-[300px] overflow-y-auto">
                  {categories.map(category => (
                    <div key={category} className="border-b last:border-b-0">
                      <div
                        className="flex items-center justify-between px-3 py-2 bg-muted/30 cursor-pointer hover:bg-muted/50"
                        onClick={() => toggleCategory(`dialog-${category}`)}
                      >
                        <div className="flex items-center gap-2">
                          {expandedCategories.includes(`dialog-${category}`) ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                          <span className="text-sm font-medium capitalize">{category}</span>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                          {filteredPermissionsByCategory[category]?.filter(p =>
                            selectedPermissions.includes(p.id)
                          ).length || 0} / {filteredPermissionsByCategory[category]?.length || 0}
                        </Badge>
                      </div>
                      {expandedCategories.includes(`dialog-${category}`) && (
                        <div className="p-2 space-y-1">
                          {filteredPermissionsByCategory[category]?.map(permission => (
                            <div
                              key={permission.id}
                              className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/30 cursor-pointer"
                              onClick={() => togglePermission(permission.id)}
                            >
                              <Checkbox
                                checked={selectedPermissions.includes(permission.id)}
                                onCheckedChange={() => togglePermission(permission.id)}
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm">{permission.name}</p>
                                <p className="text-xs text-muted-foreground truncate">
                                  {permission.code}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  {selectedPermissions.length} permissions selected
                </p>
              </div>
            </div>
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setRoleDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveRole}
              disabled={!roleForm.name.trim() || roleSaving}
            >
              {roleSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingRole ? 'Save Changes' : 'Create Role'}
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Custom Role</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the role "{roleToDelete?.name}"?
              Users assigned to this role will need to be reassigned.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteRole}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleting}
            >
              {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
